import {
  Autocomplete,
  Chip,
  InputAdornment,
  TextField as TextFieldMui,
  useTheme,
} from "@mui/material";
import { getAutocompleteText } from "portal/utils/forms";
import { UnassociatedVeselkaCrop } from "portal/state/portal/carbonCrops";
import { useTranslation } from "react-i18next";
import {
  veselkaCropAssociationName,
  VeselkaCropLabel,
} from "./VeselkaCropLabel";
import React, { FunctionComponent, useMemo } from "react";
import VeselkaIcon from "portal/images/icons/veselka.svg?react";
import WarningIcon from "@mui/icons-material/WarningAmber";

export interface VeselkaCropAssociationsFieldProps {
  value: string[];
  onChange: (veselkaCropIds: string[]) => void;
  options: UnassociatedVeselkaCrop[];
  loading: boolean;
  loadingError?: boolean;
  disabled: boolean;
}

export const VeselkaCropAssociationsField: FunctionComponent<
  VeselkaCropAssociationsFieldProps
> = ({ value, onChange, options, loading, loadingError = false, disabled }) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();

  // Autocomplete only renders selected chips that also exist in `options`.
  // When Veselka is down (or an associated id is missing from the fetch),
  // stub those ids so existing associations still show as chips.
  const resolvedOptions = useMemo(() => {
    const knownIds = new Set(options.map((crop) => crop.id));
    const missing = value
      .filter((id) => !knownIds.has(id))
      .map(
        (id): UnassociatedVeselkaCrop => ({
          id,
          commonName: id,
          description: "",
          archived: false,
        })
      );
    return missing.length === 0 ? options : [...options, ...missing];
  }, [options, value]);

  const selectedCrops = useMemo(
    () => resolvedOptions.filter((crop) => value.includes(crop.id)),
    [resolvedOptions, value]
  );

  return (
    <Autocomplete
      multiple
      options={resolvedOptions}
      value={selectedCrops}
      loading={loading}
      disabled={disabled}
      onChange={(_event, newValue) => {
        onChange(newValue.map((crop) => crop.id));
      }}
      getOptionLabel={(crop) => veselkaCropAssociationName(t, crop)}
      isOptionEqualToValue={(left, right) => left.id === right.id}
      {...getAutocompleteText(t)}
      renderOption={(props, crop) => {
        const { key, ...optionProps } = props;
        return (
          <li {...optionProps} key={key}>
            <VeselkaCropLabel crop={crop} showIcon={false} />
          </li>
        );
      }}
      renderValue={(value, getItemProps) =>
        value.map((crop, index) => (
          <Chip
            {...getItemProps({ index })}
            key={crop.id}
            label={<VeselkaCropLabel crop={crop} showIcon={false} />}
            icon={<VeselkaIcon className="ml-2 shrink-0" />}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextFieldMui
          {...params}
          label={t("models.carbonCrops.fields.veselkaCrops")}
          helperText={t(
            "components.carbonCrop.CarbonCropEditor.veselkaCropsHelper"
          )}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loadingError ? (
                <InputAdornment
                  position="end"
                  className="text-sm"
                  style={{ color: muiTheme.palette.warning.main }}
                >
                  <WarningIcon />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      )}
    />
  );
};
